import { DisconnectOutlined, EnvironmentFilled, FileTextFilled, LinkOutlined } from '@ant-design/icons'
import { GraviButton, Horizontal, MaterialIcon, Texto } from '@gravitate-js/excalibrr'
import { AutoComplete, InputNumber, Radio, Switch, Tooltip } from 'antd'
import { useMemo } from 'react'

interface ManageMatchToolbarProps {
  searchType: 'invoice' | 'bol'
  onSearchTypeChange: (type: 'invoice' | 'bol') => void

  // Filter props
  searchText: string
  onSearchTextChange: (text: string) => void
  minVolume: number | null
  onMinVolumeChange: (volume: number | null) => void
  maxVolume: number | null
  onMaxVolumeChange: (volume: number | null) => void
  includeMatched: boolean
  onIncludeMatchedChange: (include: boolean) => void
  includeOnlyMatchingTerminals: boolean
  onIncludeOnlyMatchingTerminalsChange: (include: boolean) => void
  includeOnlyMatchingSuppliers: boolean
  onIncludeOnlyMatchingSuppliersChange: (include: boolean) => void

  // Data for autocomplete
  gridData?: Array<{
    invoice_number?: string
    bol_number?: string
  }>

  // Actions
  onResetFilters: () => void

  // Loading state
  isLoading: boolean

  // Conditionals
  hasInvoices: boolean
  hasBols: boolean
}

export function ManageMatchToolbar({
  searchType,
  onSearchTypeChange,
  searchText,
  onSearchTextChange,
  minVolume,
  onMinVolumeChange,
  maxVolume,
  onMaxVolumeChange,
  includeMatched,
  onIncludeMatchedChange,
  includeOnlyMatchingTerminals,
  onIncludeOnlyMatchingTerminalsChange,
  includeOnlyMatchingSuppliers,
  onIncludeOnlyMatchingSuppliersChange,
  gridData,
  onResetFilters,
  isLoading,
  hasInvoices,
  hasBols,
}: ManageMatchToolbarProps) {
  // Generate autocomplete options based on search type and current search text
  const autocompleteOptions = useMemo(() => {
    // Only show options when user has typed at least 1 character
    if (!gridData || !searchText || searchText.trim().length < 1) return []

    const searchLower = searchText.toLowerCase().trim()
    const numbers = new Set<string>()

    // Extract numbers based on search type from the grid data (which is already filtered properly)
    gridData.forEach((item) => {
      const number = searchType === 'invoice' ? item.invoice_number : item.bol_number
      if (number && number.toLowerCase().includes(searchLower)) {
        numbers.add(number)
      }
    })

    // Convert to autocomplete options, sort alphabetically, and limit to top 8 matches
    return Array.from(numbers)
      .sort()
      .slice(0, 8)
      .map((number) => ({
        value: number,
        label: number,
      }))
  }, [gridData, searchType, searchText])

  return (
    <Horizontal
      className='px-3 py-2'
      bg='elevated'
      border='bottom'
      flex='0'
      style={{ overflow: 'visible' }}
      gap={16}
      verticalCenter
      justifyContent='space-between'
    >
      {/* Left side - Title and controls */}
      <Horizontal gap={16} verticalCenter style={{ flexWrap: 'wrap' }}>
        {/* Title */}
        <Texto category='p2' weight='600'>
          Manage Match Items
        </Texto>

        {/* Quick Search */}
        <AutoComplete
          placeholder={`Search ${searchType === 'invoice' ? 'Invoice' : 'BOL'} Numbers`}
          value={searchText}
          onChange={onSearchTextChange}
          onSelect={onSearchTextChange}
          options={autocompleteOptions}
          style={{ width: 240 }}
          allowClear
          filterOption={false}
        />

        {/* Search Type Toggle */}
        <Radio.Group value={searchType} onChange={(e) => onSearchTypeChange(e.target.value)} buttonStyle='solid'>
          <Radio.Button value='bol' disabled={!hasInvoices}>
            <Horizontal verticalCenter gap={8}>
              <MaterialIcon icon='receipt_long' size={16} />
              BOLs
            </Horizontal>
          </Radio.Button>
          <Radio.Button value='invoice' disabled={!hasBols}>
            <Horizontal verticalCenter gap={8}>
              <MaterialIcon icon='description' size={16} />
              Invoices
            </Horizontal>
          </Radio.Button>
        </Radio.Group>

        {/* Volume Range */}
        <Horizontal gap={8} verticalCenter>
          <Texto category='p2' weight='500'>
            Volume:
          </Texto>
          <InputNumber
            placeholder='Min'
            value={minVolume}
            onChange={onMinVolumeChange}
            style={{ width: 80 }}
            min={0}
            formatter={(value) => (value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '')}
            parser={(value) => (value ? parseFloat(value.replace(/\$\s?|(,*)/g, '')) : null)}
          />
          <Texto category='p2' appearance='hint'>
            to
          </Texto>
          <InputNumber
            placeholder='Max'
            value={maxVolume}
            onChange={onMaxVolumeChange}
            style={{ width: 80 }}
            min={minVolume || 0}
            formatter={(value) => (value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '')}
            parser={(value) => (value ? parseFloat(value.replace(/\$\s?|(,*)/g, '')) : null)}
          />
          <Texto category='p2' appearance='hint'>
            gallons
          </Texto>
        </Horizontal>

        {/* Include Matched Toggle */}
        <Tooltip title={includeMatched ? 'Showing already matched items' : 'Hiding already matched items'}>
          <Switch
            checked={includeMatched}
            onChange={onIncludeMatchedChange}
            checkedChildren={<LinkOutlined />}
            unCheckedChildren={<DisconnectOutlined />}
          />
        </Tooltip>

        {/* Include Only Matching Terminals Toggle */}
        <Tooltip
          title={
            includeOnlyMatchingTerminals
              ? 'Only showing items from terminals in this match'
              : 'Showing items from all terminals'
          }
        >
          <Switch
            checked={includeOnlyMatchingTerminals}
            onChange={onIncludeOnlyMatchingTerminalsChange}
            checkedChildren={<EnvironmentFilled />}
            unCheckedChildren={<EnvironmentFilled />}
          />
        </Tooltip>
        <Tooltip
          title={
            includeOnlyMatchingSuppliers
              ? 'Only showing items from the same supplier'
              : 'Showing items from all suppliers'
          }
        >
          <Switch
            checked={includeOnlyMatchingSuppliers}
            onChange={onIncludeOnlyMatchingSuppliersChange}
            checkedChildren={<FileTextFilled />}
            unCheckedChildren={<FileTextFilled />}
          />
        </Tooltip>
      </Horizontal>

      {/* Right side controls */}
      <Horizontal gap={16} verticalCenter>
        {/* Reset Filters */}
        <GraviButton
          appearance='outlined'
          size='small'
          onClick={onResetFilters}
          icon={<MaterialIcon icon='refresh' className='pt-1' />}
        >
          Reset Filters
        </GraviButton>
      </Horizontal>
    </Horizontal>
  )
}
